import { Form } from "@unform/web";
import {
  Check,
  ChevronDown,
  ChevronRight,
  FileText,
  ListChecks,
  Loader2,
  Search,
  TextSearch,
  Trash2,
  Zap,
  Upload,
  X,
} from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Input from "~/components/RegisterForm/Input";

import RegisterFormMenu from "~/components/RegisterForm/Menu";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { Zoom, toast } from "react-toastify";
import api from "~/services/api";
import { formatter, getRegistries } from "~/functions";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { useSelector } from "react-redux";
import DatePicker from "~/components/RegisterForm/DatePicker";
import { format, parseISO } from "date-fns";
import { AlertContext } from "~/App";
import { FullGridContext } from "../../..";
import FindGeneric from "~/components/Finds/FindGeneric";
import FormData from "form-data";
import FileInput from "~/components/RegisterForm/FileInput";
import BalancePanel from "./BalancePanel";
import SettlementsModal from "./SettlementsModal";
import FeeModal from "./FeeModal";
import { saveAs } from "file-saver";

export const InputContext = createContext({});

function safeDate(value, pattern = "MM/dd/yyyy") {
  if (!value) return "-";
  try {
    return format(parseISO(value), pattern);
  } catch (err) {
    return "-";
  }
}

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const { alertBox } = useContext(AlertContext);
  const {
    handleOpened,
    setOpened,
    successfullyUpdated,
    setSuccessfullyUpdated,
  } = useContext(FullGridContext);
  const [pageData, setPageData] = useState({
    loaded: false,
    filial_id: null,
    filial: {
      name: "",
    },
    items: [],
  });

  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");

  const [conciliationId, setConciliationId] = useState(id);
  const [balance, setBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [verifyingAll, setVerifyingAll] = useState(false);
  const [itemStatus, setItemStatus] = useState({});
  const [expanded, setExpanded] = useState([]);
  const [settlementsModalItem, setSettlementsModalItem] = useState(null);
  const [feeModal, setFeeModal] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(false);

  const auth = useSelector((state) => state.auth);

  const generalForm = useRef();

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  async function handleGeneralFormSubmit(data) {
    if (successfullyUpdated) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      return;
    }
    try {
      const formData = new FormData();
      formData.append("file", data.import_file);
      formData.append("filial_id", data?.filial?.id);

      const response = await api.post(
        "/gravityconciliations/import",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        const importedId = response.data.id;
        setConciliationId(importedId);
        toast("Data imported!", { autoClose: 1000 });
        setSuccessfullyUpdated(true);
        await getPageData(importedId);
        setActiveMenu("conciliation");
        await handleVerifyAll(importedId);
      } else {
        toast("Data not imported!", { autoClose: 1000 });
        setSuccessfullyUpdated(false);
      }
    } catch (err) {
      toast(err?.response?.data?.error || "Data not imported!", {
        type: "error",
        autoClose: 3000,
      });
    }
  }

  async function handleDownloadFile() {
    if (!conciliationId || downloadingFile) return;
    setDownloadingFile(true);
    try {
      const response = await api.get(
        `/gravityconciliations/${conciliationId}/download`,
        { responseType: "blob" }
      );
      saveAs(response.data, pageData?.file?.name || "gravity.csv");
    } catch (err) {
      toast(err?.response?.data?.error || "Could not download the CSV file.", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setDownloadingFile(false);
    }
  }

  async function getBalance(targetId = conciliationId) {
    if (!targetId || targetId === "new") return;
    setLoadingBalance(true);
    try {
      const { data } = await api.get(
        `/gravityconciliations/${targetId}/balance`
      );
      setBalance(data);
    } catch (err) {
      setBalance(null);
    } finally {
      setLoadingBalance(false);
    }
  }

  async function getPageData(targetId = conciliationId) {
    if (!targetId || targetId === "new") {
      setPageData((prev) => ({ ...prev, loaded: true }));
      return;
    }
    try {
      const { data } = await api.get(`/gravityconciliations/${targetId}`);

      setPageData((prev) => ({
        ...prev,
        ...data,
        loaded: true,
      }));

      const {
        created_by,
        created_at,
        updated_by,
        updated_at,
        canceled_by,
        canceled_at,
      } = data;
      const registries = await getRegistries({
        created_by,
        created_at,
        updated_by,
        updated_at,
        canceled_by,
        canceled_at,
      });
      setRegistry(registries);
      await getBalance(targetId);
    } catch (err) {
      console.log(err);
      toast(err?.response?.data?.error || "Could not load the batch.", {
        type: "error",
        autoClose: 3000,
      });
    }
  }

  useEffect(() => {
    getPageData();
  }, []);

  async function handleVerifyAll(targetId = conciliationId) {
    const { data } = await api
      .get(`/gravityconciliations/${targetId}`)
      .catch(() => ({ data: null }));
    const items = data?.items || pageData.items || [];
    const pending = items.filter((item) => item.status !== "Verified");
    if (!pending.length) {
      toast("No pending items to verify.", { type: "info", autoClose: 2000 });
      return;
    }

    setVerifyingAll(true);
    setItemStatus(
      pending.reduce((acc, item) => ({ ...acc, [item.id]: "loading" }), {})
    );

    for (const item of pending) {
      try {
        await api.put(`/gravityconciliations-verify/${item.id}`);
        setItemStatus((prev) => ({ ...prev, [item.id]: "Verified" }));
      } catch (err) {
        setItemStatus((prev) => ({ ...prev, [item.id]: "Pending" }));
      }
    }

    setVerifyingAll(false);
    setItemStatus({});
    await getPageData(targetId);
  }

  async function handleVerifyItem(item) {
    setItemStatus((prev) => ({ ...prev, [item.id]: "loading" }));
    try {
      await api.put(`/gravityconciliations-verify/${item.id}`);
      await getPageData();
    } catch (err) {
      toast(
        err?.response?.data?.error ||
          "No automatic match found — use the search to conciliate manually.",
        { type: "warning", autoClose: 3000 }
      );
    } finally {
      setItemStatus((prev) => ({ ...prev, [item.id]: undefined }));
    }
  }

  async function handleAutopaySetup(item) {
    setItemStatus((prev) => ({ ...prev, [item.id]: "loading" }));
    try {
      await api.post(`/gravityconciliations-items/${item.id}/autopay-setup`);
      toast("Autopay setup marked!", { autoClose: 1000 });
      await getPageData();
    } catch (err) {
      toast(err?.response?.data?.error || "Could not mark autopay setup.", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setItemStatus((prev) => ({ ...prev, [item.id]: undefined }));
    }
  }

  function handleUnlink(item) {
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Are you sure you want to undo the conciliation of this item?",
      buttons: [
        { title: "Cancel", class: "cancel" },
        {
          title: "Yes",
          onPress: async () => {
            try {
              await api.delete(
                `/gravityconciliations-items/${item.id}/settlements`
              );
              toast("Conciliation undone!", { autoClose: 1000 });
              await getPageData();
            } catch (err) {
              toast(err?.response?.data?.error || "Could not undo.", {
                type: "error",
                autoClose: 3000,
              });
            }
          },
        },
      ],
    });
  }

  function handleRemoveFee() {
    alertBox({
      title: "Attention!",
      descriptionHTML: "Are you sure you want to remove the fee of this batch?",
      buttons: [
        { title: "Cancel", class: "cancel" },
        {
          title: "Yes",
          onPress: async () => {
            try {
              await api.delete(`/gravityconciliations/${conciliationId}/fee`);
              toast("Fee removed!", { autoClose: 1000 });
              await getPageData();
            } catch (err) {
              toast(err?.response?.data?.error || "Could not remove the fee.", {
                type: "error",
                autoClose: 3000,
              });
            }
          },
        },
      ],
    });
  }

  function handleDeleteBatch() {
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Are you sure you want to delete this batch?<br/><br/>All conciliations of this batch will be undone and the related receivables and payees will be released, becoming available for another batch.<br/><br/>This action cannot be undone.",
      buttons: [
        { title: "Cancel", class: "cancel" },
        {
          title: "Yes",
          onPress: async () => {
            setDeletingBatch(true);
            try {
              const { data } = await api.delete(
                `/gravityconciliations/${conciliationId}`
              );
              toast(
                `Batch ${data?.batch_number ?? ""} deleted! ${
                  data?.unlinked ?? 0
                } conciliations undone.`,
                { autoClose: 2500 }
              );
              setSuccessfullyUpdated(true);
              handleOpened(null);
            } catch (err) {
              toast(
                err?.response?.data?.error || "Could not delete the batch.",
                {
                  type: "error",
                  autoClose: 5000,
                }
              );
              setDeletingBatch(false);
            }
          },
        },
      ],
    });
  }

  function toggleExpanded(itemId) {
    setExpanded((prev) =>
      prev.includes(itemId)
        ? prev.filter((el) => el !== itemId)
        : [...prev, itemId]
    );
  }

  const hasFee = !!(pageData?.fee_payee || pageData?.feePayee);
  const pendingCount = (pageData?.items || []).filter(
    (item) => item.status !== "Verified"
  ).length;

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {settlementsModalItem ? (
        <SettlementsModal
          item={settlementsModalItem}
          onClose={() => setSettlementsModalItem(null)}
          onConciliated={() => getPageData()}
        />
      ) : null}

      {feeModal ? (
        <FeeModal
          conciliationId={conciliationId}
          suggestedAmount={balance?.fee_amount}
          onClose={() => setFeeModal(false)}
          onLaunched={() => getPageData()}
        />
      ) : null}

      {pageData ? (
        <div className="flex h-full flex-row items-start justify-between gap-4">
          <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="general"
            >
              <Upload size={16} /> Upload File
            </RegisterFormMenu>
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="conciliation"
            >
              <ListChecks size={16} /> Conciliation
            </RegisterFormMenu>
            {conciliationId && conciliationId !== "new" ? (
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="delete"
              >
                <Trash2 size={16} /> Delete Batch
              </RegisterFormMenu>
            ) : null}
          </div>
          <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
            <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
              <Form
                ref={generalForm}
                onSubmit={handleGeneralFormSubmit}
                className="w-full pb-32"
              >
                <InputContext.Provider
                  value={{
                    id: conciliationId,
                    generalForm,
                    setSuccessfullyUpdated,
                    fullscreen,
                    setFullscreen,
                    successfullyUpdated,
                    handleCloseForm,
                  }}
                >
                  {conciliationId === "new" || pageData.loaded ? (
                    <>
                      <FormHeader
                        access={access}
                        title="Gravity Conciliation"
                        registry={registry}
                        InputContext={InputContext}
                        className="relative overflow-hidden"
                      />

                      <InputLineGroup
                        title="GENERAL"
                        activeMenu={activeMenu === "general"}
                      >
                        <FindGeneric
                          route="filials"
                          title="Filial"
                          scope="filial"
                          required
                          InputContext={InputContext}
                          readOnly={pageData?.filial?.id ? true : false}
                          defaultValue={
                            conciliationId === "new" && auth.filial.id !== 1
                              ? {
                                  id: auth.filial.id,
                                  name: auth.filial.name,
                                }
                              : {
                                  id: pageData.filial?.id,
                                  name: pageData.filial?.name,
                                }
                          }
                          fields={[
                            {
                              title: "Name",
                              name: "name",
                            },
                          ]}
                        />
                        <InputLine title="Gravity Conciliation">
                          {pageData?.file ? (
                            <div className="flex flex-col items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={handleDownloadFile}
                                disabled={downloadingFile}
                                title="Download original CSV"
                                className="bg-slate-100 w-36 min-h-36 flex flex-col items-center justify-center gap-2 border rounded-md p-2 text-center text-xs text-slate-500 hover:border-primary hover:text-primary cursor-pointer"
                              >
                                {downloadingFile ? (
                                  <Loader2 size={32} className="animate-spin" />
                                ) : (
                                  <FileText size={32} strokeWidth={1.5} />
                                )}
                                <h3 className="w-full font-bold">
                                  {pageData?.file?.name}
                                </h3>
                                <p>
                                  {(pageData?.file?.size / 1024).toFixed(2)}KB
                                </p>
                              </button>
                            </div>
                          ) : (
                            <FileInput
                              name="import_file"
                              title="File"
                              grow
                              required
                              InputContext={InputContext}
                              fields={[
                                {
                                  title: "Name",
                                  name: "name",
                                },
                              ]}
                            />
                          )}
                        </InputLine>
                      </InputLineGroup>

                      <InputLineGroup
                        title="Conciliation"
                        activeMenu={activeMenu === "conciliation"}
                        className="relative"
                      >
                        <InputLine title="Information">
                          <DatePicker
                            name="date"
                            required
                            title="Date"
                            readOnly
                            defaultValue={
                              pageData?.date
                                ? parseISO(pageData?.date)
                                : new Date()
                            }
                            placeholderText="MM/DD/YYYY"
                            InputContext={InputContext}
                          />
                          <Input
                            type="text"
                            name="batch_number"
                            title="Batch Number"
                            shrink
                            readOnly
                            defaultValue={pageData?.batch_number}
                            InputContext={InputContext}
                          />
                          <Input
                            type="text"
                            name="status"
                            title="Status"
                            grow
                            readOnly
                            defaultValue={pageData?.status}
                            className={
                              pageData?.status === "Concluded"
                                ? "text-emerald-500"
                                : ""
                            }
                            InputContext={InputContext}
                          />
                        </InputLine>

                        <InputLine title="Balance">
                          <div className="w-full pt-2">
                            <BalancePanel
                              balance={balance}
                              loading={loadingBalance}
                              hasFee={hasFee}
                              canLaunchFee={pageData?.status !== "Concluded"}
                              onLaunchFee={() => setFeeModal(true)}
                              onRemoveFee={handleRemoveFee}
                            />
                          </div>
                        </InputLine>

                        {pendingCount > 0 ? (
                          <InputLine title="Actions">
                            <button
                              type="button"
                              disabled={verifyingAll}
                              className="bg-primary text-white rounded-md p-1 px-2 h-8 mt-3 flex flex-row items-center justify-center text-xs gap-1 hover:opacity-80 disabled:opacity-50 transition-all"
                              onClick={() => handleVerifyAll()}
                            >
                              {verifyingAll ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <TextSearch size={16} />
                              )}
                              Verify all registries ({pendingCount} pending)
                            </button>
                          </InputLine>
                        ) : null}

                        <table className="w-full text-xs">
                          <thead className="sticky top-0 border-b">
                            <tr className="bg-secondary h-8">
                              <th className="w-8" />
                              <th>Settled On</th>
                              <th>Approval</th>
                              <th>Type</th>
                              <th>Name</th>
                              <th>Reference</th>
                              <th>Amount</th>
                              <th>Status</th>
                              <th className="w-16">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="align-center">
                            {pageData?.items?.map((item) => {
                              const status = itemStatus[item.id] || item.status;
                              const isVerified = status === "Verified";
                              const isLoading = status === "loading";
                              const settlements = item.settlements || [];
                              const isAutopay = settlements.some(
                                (link) => link.memo === "Autopay setup"
                              );
                              const isManual =
                                !isAutopay &&
                                settlements.some((link) => link.manual);
                              const isExpanded = expanded.includes(item.id);

                              return (
                                <React.Fragment key={item.id}>
                                  <tr
                                    className={`border-b ${
                                      isVerified
                                        ? "text-emerald-600"
                                        : "text-red-600"
                                    }`}
                                  >
                                    <td className="py-1 text-center">
                                      {isVerified && settlements.length > 0 ? (
                                        <button
                                          type="button"
                                          onClick={() => toggleExpanded(item.id)}
                                        >
                                          {isExpanded ? (
                                            <ChevronDown size={14} />
                                          ) : (
                                            <ChevronRight size={14} />
                                          )}
                                        </button>
                                      ) : null}
                                    </td>
                                    <td className="text-center">
                                      {safeDate(item.settled_on)}
                                    </td>
                                    <td className="text-center">
                                      {item.approval}
                                    </td>
                                    <td className="text-center">
                                      {item.type || "-"}
                                    </td>
                                    <td className="text-center">
                                      {item.name || "-"}
                                    </td>
                                    <td className="text-center">
                                      {item.reference || "-"}
                                    </td>
                                    <td className="text-center">
                                      {formatter.format(
                                        Number(item.amount || 0)
                                      )}
                                    </td>
                                    <td className="text-center">
                                      <div className="flex flex-row items-center justify-center gap-1">
                                        {isLoading ? (
                                          <Loader2
                                            size={14}
                                            className="animate-spin"
                                          />
                                        ) : isVerified ? (
                                          <Check size={14} />
                                        ) : null}
                                        {isLoading
                                          ? "Verifying"
                                          : isVerified
                                          ? "Verified"
                                          : "Pending"}
                                        {isAutopay ? (
                                          <span className="text-gray-400">
                                            (autopay setup)
                                          </span>
                                        ) : null}
                                        {isManual ? (
                                          <span
                                            className="text-gray-400"
                                            title={
                                              settlements.find(
                                                (link) => link.memo
                                              )?.memo || "Manual"
                                            }
                                          >
                                            (manual)
                                          </span>
                                        ) : null}
                                      </div>
                                    </td>
                                    <td>
                                      <div className="flex flex-row items-center justify-center gap-2">
                                        {isLoading ? null : isVerified ? (
                                          <button
                                            type="button"
                                            title="Undo conciliation"
                                            onClick={() => handleUnlink(item)}
                                            className="hover:scale-125 transition-all"
                                          >
                                            <X size={14} />
                                          </button>
                                        ) : (
                                          <>
                                            <button
                                              type="button"
                                              title="Try automatic match"
                                              disabled={verifyingAll}
                                              onClick={() =>
                                                handleVerifyItem(item)
                                              }
                                              className="hover:scale-125 transition-all disabled:opacity-40"
                                            >
                                              <TextSearch size={14} />
                                            </button>
                                            <button
                                              type="button"
                                              title={
                                                verifyingAll
                                                  ? "Wait for the automatic verification to finish"
                                                  : "Conciliate manually"
                                              }
                                              disabled={verifyingAll}
                                              onClick={() =>
                                                setSettlementsModalItem(item)
                                              }
                                              className="hover:scale-125 transition-all disabled:opacity-40"
                                            >
                                              <Search size={14} />
                                            </button>
                                            <button
                                              type="button"
                                              title="Autopay setup"
                                              disabled={verifyingAll}
                                              onClick={() =>
                                                handleAutopaySetup(item)
                                              }
                                              className="hover:scale-125 transition-all disabled:opacity-40"
                                            >
                                              <Zap size={14} />
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  </tr>

                                  {isExpanded
                                    ? settlements.map((link) => {
                                        const settlement =
                                          link.settlement ||
                                          link.payeesettlement ||
                                          {};
                                        const refund = settlement.refund;
                                        const receivable =
                                          settlement.receivable ||
                                          refund?.receivable;
                                        const issuer =
                                          receivable?.issuer ||
                                          settlement.payee?.issuer;
                                        const student = issuer?.student;
                                        const transaction =
                                          refund?.emergepaytransaction;
                                        return (
                                          <tr
                                            key={link.id}
                                            className="text-gray-600 bg-gray-50 border-b"
                                          >
                                            <td />
                                            <td className="text-center">
                                              {safeDate(
                                                settlement.settlement_date
                                              )}
                                            </td>
                                            <td className="text-center">
                                              {transaction?.approval_number_result ||
                                                "-"}
                                            </td>
                                            <td className="text-center">
                                              {refund ? "Refund" : "Payment"}
                                            </td>
                                            <td className="text-center">
                                              {student
                                                ? `${student.name || ""} ${
                                                    student.last_name || ""
                                                  }`.trim()
                                                : settlement.payee?.memo ||
                                                  issuer?.name ||
                                                  "-"}
                                            </td>
                                            <td className="text-center">
                                              {receivable?.invoice_number ||
                                                transaction?.transaction_reference ||
                                                "-"}
                                            </td>
                                            <td className="text-center">
                                              {formatter.format(
                                                link.payeesettlement
                                                  ? -Math.abs(Number(settlement.amount || 0))
                                                  : Number(settlement.amount || 0)
                                              )}
                                            </td>
                                            <td
                                              colSpan={2}
                                              className="text-center"
                                            >
                                              {link.manual
                                                ? `Manual${
                                                    link.memo
                                                      ? `: ${link.memo}`
                                                      : ""
                                                  }`
                                                : link.memo ||
                                                  (transaction
                                                    ? "Automatic match by Gravity transaction"
                                                    : "Automatic match")}
                                            </td>
                                          </tr>
                                        );
                                      })
                                    : null}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </InputLineGroup>

                      <InputLineGroup
                        title="DELETE BATCH"
                        activeMenu={activeMenu === "delete"}
                      >
                        <InputLine title="Delete Batch" left>
                          <div className="w-full flex flex-col gap-3 pt-2 text-xs">
                            <p>
                              Deleting this batch will undo every conciliation
                              made in it. All receivables and payees linked to
                              this batch will be released and become available
                              for another batch.
                            </p>
                            <p>
                              The fee payee of this batch, if any, will be
                              canceled. After deleting, the same file can be
                              imported again.
                            </p>
                            <p className="text-red-600 font-bold">
                              This action cannot be undone.
                            </p>
                            <div>
                              <button
                                type="button"
                                disabled={deletingBatch}
                                className="bg-red-600 text-white rounded-md p-1 px-2 h-8 flex flex-row items-center justify-center text-xs gap-1 hover:opacity-80 disabled:opacity-50 transition-all"
                                onClick={() => handleDeleteBatch()}
                              >
                                {deletingBatch ? (
                                  <Loader2 size={16} className="animate-spin" />
                                ) : (
                                  <Trash2 size={16} />
                                )}
                                Delete batch
                              </button>
                            </div>
                          </div>
                        </InputLine>
                      </InputLineGroup>
                    </>
                  ) : (
                    <FormLoading />
                  )}
                </InputContext.Provider>
              </Form>
            </div>
          </div>
        </div>
      ) : null}
    </Preview>
  );
}
